import { useState, useEffect } from 'react';
import { isBeforeWebinar, computeTimerPlan } from './promoVisibility';
import { SECOND, MINUTE, HOUR, DAY } from 'utils/timeConstants';

export interface CountdownResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export function computeCountdown(targetMs: number): CountdownResult {
  const remaining = targetMs - Date.now();
  if (remaining <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }
  const days = Math.floor(remaining / DAY);
  const hours = Math.floor((remaining % DAY) / HOUR);
  const minutes = Math.floor((remaining % HOUR) / MINUTE);
  const seconds = Math.floor((remaining % MINUTE) / SECOND);
  return { days, hours, minutes, seconds, isExpired: false };
}

export function useCountdown(targetIsoString: string): CountdownResult {
  const targetMs = new Date(targetIsoString).getTime();

  const [state, setState] = useState<CountdownResult>(() => computeCountdown(targetMs));

  useEffect(() => {
    const initialState = computeCountdown(targetMs);
    if (initialState.isExpired) return;

    const id = setInterval(() => {
      const next = computeCountdown(targetMs);
      setState(next);
      if (next.isExpired) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);

  return state;
}

export const MAX_DELAY = 2_000_000_000;

export function useWebinarActive(targetIsoString: string): boolean {
  const targetMs = new Date(targetIsoString).getTime();
  const [active, setActive] = useState<boolean>(() => isBeforeWebinar(Date.now(), targetMs));
  // arm is incremented to re-trigger the effect when a clamped timeout fires
  // and the target has not yet been reached.
  const [arm, setArm] = useState(0);

  useEffect(() => {
    if (!isBeforeWebinar(Date.now(), targetMs)) {
      setActive(false);
      return;
    }

    const plan = computeTimerPlan(Date.now(), targetMs, MAX_DELAY);
    const id = setTimeout(() => {
      if (plan.reachesTarget && Date.now() >= targetMs) {
        setActive(false);
      } else {
        // Delay was clamped; re-arm by bumping the counter so this effect runs again.
        setArm((n) => n + 1);
      }
    }, plan.delay);

    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs, arm]);

  return active;
}
